import {
    Input,
    Form,
    Space,
    Button,
    Table,
    Result,
    Skeleton,
    Dropdown,
    message,
    Modal,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { DownloadOutlined, DownOutlined } from '@ant-design/icons';
import {
    OPORTUNIDADES_ENDPOINT,
    createRegistroAccion,
    getOportunidadById,
} from '../../services/api/oportunidad';
import { useEffect, useState } from 'react';
import { DefaultPaginationConfig } from '../../constants/pagination';
import ArchivoList from '../../components/archivo-list';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import {
    GENERIC_ERROR_MESSAGE,
    GENERIC_SUCCESS_MESSAGE,
} from '../../constants/feedback';
import { SUB_ACCION_PARTICIPA } from '../../constants/sub-accion';
import useSWRImmutable from 'swr/immutable';
import {
    SUB_ACCIONES_ENDPOINT_KVP_ENDPOINT,
    getSubAccionesByAccionAsKvpList,
} from '../../services/api/sub-accion';
import { ACCION_DESCARTAR } from '../../constants/accion';

const itemLayout = {
    labelCol: { span: 24 },
};

const horizontalItemLayout = {
    labelCol: {
        flex: '140px',
    },
    labelAlign: 'left',
};

const OportunidadItem = (props) => {
    const [form] = Form.useForm();
    const [formattedPresupuesto, setFormattedPresupuesto] = useState('0');
    const [items, setItems] = useState([]);
    const [messageApi, contextHolder] = message.useMessage();
    const [creating, setCreating] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const key = 'updatable';

    const { data: subAcciones } = useSWRImmutable(
        [SUB_ACCIONES_ENDPOINT_KVP_ENDPOINT, ACCION_DESCARTAR],
        () => getSubAccionesByAccionAsKvpList(ACCION_DESCARTAR)
    );

    const { data: oportunidad, isLoading } = useSWR(
        props.idOportunidad > 0
            ? [OPORTUNIDADES_ENDPOINT, props.idOportunidad]
            : null,
        ([_, idOportunidad]) => getOportunidadById(idOportunidad)
    );

    useEffect(() => {
        if (oportunidad?.data.idOportunidad > 0) {
            let fields = Object.keys(oportunidad.data).map((key) => ({
                name: key,
                value: oportunidad.data[key],
            }));

            setFormattedPresupuesto(
                oportunidad.data.presupuesto.toLocaleString()
            );

            const fechaInicio = new Date(oportunidad.data.fechaInicio);
            const fechaCierre = new Date(oportunidad.data.fechaCierre);

            fields.push(
                {
                    name: 'formattedFechaInicio',
                    value: `${fechaInicio.toLocaleDateString()} ${fechaInicio.toLocaleTimeString()}`,
                },
                {
                    name: 'formattedFechaCierre',
                    value: `${fechaCierre.toLocaleDateString()} ${fechaCierre.toLocaleTimeString()}`,
                },
                {
                    name: 'formattedPoseeMulta',
                    value: oportunidad.data.flagOpSujetaMulta ? 'Si' : 'No',
                }
            );

            form.setFields(fields);
        }
    }, [oportunidad, form]);

    useEffect(() => {
        if (subAcciones?.data) {
            const tempItems = subAcciones.data.map((item) => ({
                key: item.idSubAccion,
                label: item.descripcion,
            }));

            setItems(tempItems);
        }
    }, [subAcciones]);

    const columns = [
        {
            title: 'Descripción',
            dataIndex: 'referenciaProducto1',
            sorter: (a, b) =>
                a.referenciaProducto1 < b.referenciaProducto1 ? -1 : 1,
            width: '80%',
        },
        {
            title: 'Cantidad',
            dataIndex: 'cantidad',
            sorter: (a, b) => a.cantidad - b.cantidad,
            width: '20%',
        },
    ];

    const handleParticipeClick = async (e) => {
        messageApi.open({
            key,
            type: 'loading',
            content: 'Registrando acción...',
            duration: 0,
        });

        try {
            setCreating(true);

            const idOportunidad = oportunidad?.data.idOportunidad;
            const idSubAccion = SUB_ACCION_PARTICIPA;

            await createRegistroAccion(idOportunidad, idSubAccion);

            messageApi.open({
                key,
                type: 'success',
                content: GENERIC_SUCCESS_MESSAGE,
            });

            props.onCancel();
        } catch (error) {
            messageApi.open({
                key,
                type: 'error',
                content: GENERIC_ERROR_MESSAGE,
            });
        } finally {
            setCreating(false);
        }
    };

    const handleDescarteClick = async (e) => {
        messageApi.open({
            key,
            type: 'loading',
            content: 'Registrando acción...',
            duration: 0,
        });

        try {
            setCreating(true);

            const idOportunidad = oportunidad?.data.idOportunidad;
            const idSubAccion = e.key;

            await createRegistroAccion(idOportunidad, idSubAccion);

            messageApi.open({
                key,
                type: 'success',
                content: GENERIC_SUCCESS_MESSAGE,
            });

            props.onCancel();
        } catch (error) {
            messageApi.open({
                key,
                type: 'error',
                content: GENERIC_ERROR_MESSAGE,
            });
        } finally {
            setCreating(false);
        }
    };

    const handleFilesClick = () => {
        setIsModalOpen(true);
    };

    const handleCancelModal = () => {
        setIsModalOpen(false);
    };

    if (!isLoading && !oportunidad?.data) {
        return (
            <Result
                status='404'
                title='Oportunidad'
                subTitle='Lo sentimos, la oportunidad no fue encontrada.'
                extra={
                    <Link to='/oportunidades'>
                        <Button type='primary'>Volver</Button>
                    </Link>
                }
            />
        );
    }

    const handleCancelClick = () => {
        props.onCancel();
    };

    const TitleComponent = () => (
        <ul className='breadcrumb d-flex justify-content-between'>
            <li>
                <h6>Oportunidad: {oportunidad?.data.codigoOp}</h6>
            </li>
            <li>
                <h6 className='txt-color-primary float-end me-2'>
                    PPTO: $ {formattedPresupuesto}
                </h6>
            </li>
        </ul>
    );

    return (
        <>
            <Modal
                open={props.isModalOpen}
                title={<TitleComponent />}
                onCancel={handleCancelClick}
                destroyOnClose={true}
                maskClosable={false}
                footer={[
                    <Button key='back' onClick={handleCancelClick}>
                        Cancelar
                    </Button>,
                ]}
                style={{ top: 20 }}
                width={800}
            >
                {contextHolder}

                <div className='row'>
                    <div className='col-lg-12'>
                        <div className='card card rounded-4 shadow'>
                            <div className='card-body shadow-sm rounded-4'>
                                {isLoading && (
                                    <Skeleton active className='pt-2' />
                                )}

                                {!isLoading && (
                                    <Form
                                        form={form}
                                        disabled
                                        className='form-cola-trabajo'
                                        size='small'
                                    >
                                        <div className='row pl-2 pr-2'>
                                            <div className='col-lg-12'>
                                                <Form.Item
                                                    name='nombre'
                                                    label='Título Oferta'
                                                    {...itemLayout}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </div>

                                            <div className='col-lg-12'>
                                                <Form.Item
                                                    name='descripcion'
                                                    label='Descripción Oferta'
                                                    {...itemLayout}
                                                >
                                                    <TextArea rows={4} />
                                                </Form.Item>
                                            </div>

                                            <div className='col-lg-12'>
                                                <Form.Item
                                                    name='direccion'
                                                    label='Dirección'
                                                    {...itemLayout}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </div>

                                            <div className='col-lg-6'>
                                                <Form.Item
                                                    name='cliente'
                                                    label='Cliente'
                                                    {...itemLayout}
                                                >
                                                    <Input disabled />
                                                </Form.Item>
                                            </div>

                                            <div className='col-lg-6'>
                                                <Form.Item
                                                    name='unidad'
                                                    label='Unidad'
                                                    {...itemLayout}
                                                >
                                                    <Input disabled />
                                                </Form.Item>
                                            </div>

                                            <div className='col-lg-6'>
                                                <Form.Item
                                                    name='datosContacto'
                                                    label='Datos de Contacto:'
                                                    {...itemLayout}
                                                    className='mb-0'
                                                ></Form.Item>
                                                <Form.Item
                                                    name='nombreContacto'
                                                    label='Nombre'
                                                    {...horizontalItemLayout}
                                                >
                                                    <Input disabled />
                                                </Form.Item>
                                                <Form.Item
                                                    name='telefonoContacto'
                                                    label='Teléfono'
                                                    {...horizontalItemLayout}
                                                >
                                                    <Input disabled />
                                                </Form.Item>
                                                <Form.Item
                                                    name='correoContacto'
                                                    label='E-mail'
                                                    {...horizontalItemLayout}
                                                >
                                                    <Input disabled />
                                                </Form.Item>
                                            </div>

                                            <div className='col-lg-6'>
                                                <Form.Item
                                                    name='fecha'
                                                    label='Fechas:'
                                                    {...itemLayout}
                                                    className='mb-0'
                                                ></Form.Item>

                                                <Form.Item
                                                    name='formattedFechaInicio'
                                                    label='Fecha de Publicación'
                                                    {...horizontalItemLayout}
                                                >
                                                    <Input disabled />
                                                </Form.Item>
                                                <Form.Item
                                                    name='formattedFechaCierre'
                                                    label='Fecha de Cierre'
                                                    {...horizontalItemLayout}
                                                >
                                                    <Input disabled />
                                                </Form.Item>
                                                <Form.Item
                                                    name='plazoEntrega'
                                                    label='Plazo de Entrega'
                                                    {...horizontalItemLayout}
                                                >
                                                    <Input disabled />
                                                </Form.Item>
                                            </div>

                                            <div className='col-lg-6'>
                                                <Form.Item
                                                    name='formattedPoseeMulta'
                                                    label='¿Posee Multa?'
                                                    {...itemLayout}
                                                >
                                                    <Input disabled />
                                                </Form.Item>
                                            </div>

                                            <div className='col-lg-6'>
                                                <Form.Item
                                                    name='qlineas'
                                                    label='Qty Líneas'
                                                    {...itemLayout}
                                                >
                                                    <Input disabled />
                                                </Form.Item>
                                            </div>

                                            <div className='col-lg-6  mb-0 mt-3'>
                                                <Space>
                                                    <Button
                                                        type='primary'
                                                        icon={
                                                            <DownloadOutlined />
                                                        }
                                                        onClick={
                                                            handleFilesClick
                                                        }
                                                        disabled={false}
                                                    >
                                                        Archivos
                                                    </Button>
                                                </Space>
                                            </div>

                                            <div className='col-lg-6 text-end mt-3'>
                                                <Form.Item className='mb-0'>
                                                    <Space>
                                                        <Button
                                                            type='primary'
                                                            onClick={
                                                                handleParticipeClick
                                                            }
                                                            disabled={creating}
                                                        >
                                                            Participe
                                                        </Button>

                                                        <Dropdown
                                                            menu={{
                                                                items,
                                                                onClick:
                                                                    handleDescarteClick,
                                                            }}
                                                            disabled={creating}
                                                        >
                                                            <Button type='primary'>
                                                                <Space>
                                                                    Descarte
                                                                    <DownOutlined />
                                                                </Space>
                                                            </Button>
                                                        </Dropdown>
                                                    </Space>
                                                </Form.Item>
                                            </div>
                                        </div>
                                    </Form>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className='col-lg-12 pt-4 pb-3'>
                    <div className='card card rounded-4 shadow p-2'>
                        <Table
                            rowKey={(record) => record.idDetalleOportunidad}
                            dataSource={oportunidad?.data.detalles}
                            columns={columns}
                            pagination={{
                                ...DefaultPaginationConfig,
                            }}
                            loading={isLoading}
                            className='mt-3'
                        ></Table>
                    </div>
                </div>

                <ArchivoList
                    archivos={oportunidad?.data.archivos}
                    isModalOpen={isModalOpen}
                    onCancel={handleCancelModal}
                />
            </Modal>
        </>
    );
};

export default OportunidadItem;
